import React, { FC, useContext, useEffect, useState } from 'react';
import { colorConstants } from 'config/colorConstants';
import { Form, Spin, Tabs } from 'antd';
import { TPODataContext } from 'contexts/TPOContext';

import { CustomModal } from '../../PricingSimulator/Modals/CreateEditProjectContainer';
import dayjs, { Dayjs } from 'dayjs';

//updating antd config from moment to dayjs
//antd uses moment js by default for reading value
import dayjsGenerateConfig from 'rc-picker/lib/generate/dayjs';
import generatePicker from 'antd/es/date-picker/generatePicker';
import { antIcon } from 'components/pages/Login';
import EventDetails from '../AddEventModal/EventDetails';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { tradePromoOptimizationUrls } from 'config/urls';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { formatDatePreservingTimeValues, sendNotification } from 'utils/helperMethods';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { ErrorMessages, tpoNotificationMessages } from 'utils/Enums';
import { queryClient } from 'services/reactQueryService';
import { AttributesPromoMapping } from 'types/tradePromoOptimization';
import { selectAllObject } from '../AddEventModal/PPGSelection';
//updated component that uses dayjs instead of moment

type Props = {
    open: boolean;
    onCancel: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onSubmitFinish: Function;
    eventId: null | string;
    disabled?: boolean;
    calenderDataCount: number;
};

const AddOrEditEventModal: FC<Props> = ({
    open,
    onCancel,
    onSubmitFinish,
    eventId,
    disabled = false,
    calenderDataCount,
}) => {
    const { selectedPlanInfo, setSelectedEventInfo, selectedEventInfo, isTpmTab } = useContext(TPODataContext);

    const [nestedAttributePromoMapping, setNestedAttributePromoMapping] = useState<AttributesPromoMapping>({});

    //antd form
    const [form] = Form.useForm();

    // Track form validation errors to disable submit button
    const [hasFormErrors, setHasFormErrors] = useState(false);

    // Watch for form field changes to validate
    const formValues = Form.useWatch([], form);

    // Check for form errors whenever form values change
    useEffect(() => {
        if (formValues) {
            form.validateFields()
                .then(() => setHasFormErrors(false))
                .catch(() => setHasFormErrors(true));
        }
    }, [formValues, form]);

    const modalTitle = `${eventId ? 'Edit' : 'Add'} Event`;
    const modalButtonTitle = `${eventId ? 'Save' : 'Add'} Event`;

    const { mutate: createEditEvent, loading: createEditEventLoading } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.createEditEventUrl,
        method: 'post',
    });

    useEffect(() => {
        //cleanup function
        return () => {
            setNestedAttributePromoMapping({});
            queryClient.removeQueries({
                queryKey: ['tpo-event-data-by-id', { eventId }],
                exact: true,
            });
        };
    }, [open]);
    //get event meta details to fill the form in edit flow
    const { loading: eventDataLoading, error: eventDataError } = useCommonApiHandler({
        method: 'get',
        queryConfig: { enabled: open && Boolean(eventId) },
        queryKey: ['tpo-event-data-by-id', { eventId }],
        url: `${tradePromoOptimizationUrls.getEventDataByIdUrl}/${eventId}`,
        parserFunction: (res: any) => {
            const startDateFormatted = res?.start_date
                ? dayjs(formatDatePreservingTimeValues(res?.start_date), 'MM/DD/YYYY')
                : null;
            const endDateFormatted = res?.end_date
                ? dayjs(formatDatePreservingTimeValues(res?.end_date), 'MM/DD/YYYY')
                : null;
            // const duration = endDateFormatted.diff(startDateFormatted, 'day');

            // const offer =
            //     res?.offer_type === 'manual'
            //         ? res?.offer_details?.manual_details?.value ?? [
            //               res?.offer_details?.manual_details?.buy,
            //               res?.offer_details?.manual_details?.get,
            //           ]
            //         : res?.offer_details?.selected_option;
            // const offerType = res?.offer_type === 'manual' ? res?.offer_details?.manual_details?.type : undefined; //offer type is only applicable for manual offer method
            // const offerTypeUnit =
            //     res?.offer_details?.manual_details?.is_value_percentage ||
            //     res?.offer_details?.manual_details?.is_combo_percentage
            //         ? '%'
            //         : 'abs';

            //stores nested attribute promo mapping
            // this is used to show warning when user tries to remove ppg/material group in edit event flow
            const tempAttributesPromoMapping: AttributesPromoMapping = {};

            const tempState = {
                name: res?.event_name,
                // offerMethod: res?.offer_type,
                // offer: offer,
                // offerType: offerType,
                // offerTypeUnit: offerTypeUnit,
                displayACV: res?.display_acv,
                featureACV: res?.feature_acv,
                selectedDim1Attributes: res?.selected_attribute?.map((item: string) => {
                    return {
                        label: item,
                        value: item,
                    };
                }),
                selectedDim1NestedAttributes: res?.selected_attribute_nested?.map((item: any) => {
                    tempAttributesPromoMapping[item?.dim_id ?? item?.value] = item?.promo_names ?? [];
                    return {
                        label: item?.value,
                        value: item?.dim_id ?? item?.value,
                    };
                }),
                CRAStartDate: startDateFormatted,
                CRAEndDate: endDateFormatted,
                contract: res?.contract,
                // CRADuration: duration,
                // newStatus: res?.new_status,
                // par: res?.par,
                // promoGrouping: res?.custom_promo_grouping,
                createdBy: res?.created_by,
            };
            setNestedAttributePromoMapping(tempAttributesPromoMapping);
            setSelectedEventInfo({ ...(selectedEventInfo ?? {}), ...tempState });
            return {};
        },
    });

    const onSubmit = () => {
        if (createEditEventLoading || eventDataLoading || (eventId && eventDataError) || disabledEvent) return;
        //validate form and trigger api call
        form.validateFields()
            .then((values) => {
                //product
                const nestedAttribute =
                    values?.selectedDim1NestedValues
                        ?.filter((item: any) => item?.value !== selectAllObject.value) //remove select all
                        .map((item: any) => {
                            return {
                                // label: item,
                                value: item?.label,
                                dim_id: item?.value,
                                shipper_stock: null,
                                open_stock: null,
                            };
                        }) ?? [];

                //brand
                const parentAttribute =
                    values?.selectedDim1Values?.map((item: any) => {
                        return {
                            // label: item,
                            value: item?.label,
                            dim_id: item?.value,
                            shipper_stock: null,
                            open_stock: null,
                        };
                    }) ?? [];

                const payload = {
                    plan_id: selectedPlanInfo?.planId,
                    event_id: eventId ?? null,
                    event_name: values?.name,

                    display_acv: 80, //isNaN(values?.displayACV) || !values?.displayACV ? null : Number(values?.displayACV),
                    feature_acv: 80, // isNaN(values?.featureACV) || !values?.featureACV ? null : Number(values?.featureACV),
                    created_by: localStorage.getItem('email'),
                    selected_attribute_nested: nestedAttribute,
                    is_tpm_plan: selectedPlanInfo?.isTpmPlan,
                    is_tpm_tab: isTpmTab,
                    event_type: 'tpo',
                };
                createEditEvent(payload, {
                    onSuccess: (data: any) => {
                        sendNotification(
                            'success',
                            eventId
                                ? tpoNotificationMessages.EDIT_EVENT_SUCCESS
                                : tpoNotificationMessages.CREATE_EVENT_SUCCESS,
                        );
                        onSubmitFinish();
                    },
                    onError: (res: any) => {
                        sendNotification('error', res?.response?.data?.detail || ErrorMessages.GENERALIZED_ERROR_MSG);
                        // onCancel(val);
                    },
                });
            })
            .catch((errorInfo) => {
                console.log('logging error');
            });
        // form.submit();
    };
    //  const disabledEvent = eventId ? selectedEventInfo?.createdBy === localStorage.getItem('email') : true;
    const disabledEvent = disabled || false;

    return (
        <div>
            <CustomModal
                style={{ borderRadius: 8 }}
                cancelButtonProps={{ disabled: createEditEventLoading, style: { borderRadius: 6 } }}
                okButtonProps={{
                    disabled: disabledEvent || hasFormErrors,
                    htmlType: 'submit',
                    style: { borderRadius: 6 },
                }}
                okText={
                    createEditEventLoading || eventDataLoading ? (
                        <Spin indicator={antIcon} style={{ color: 'white' }} />
                    ) : (
                        modalButtonTitle
                    )
                }
                maskClosable={false}
                destroyOnClose
                centered
                onOk={onSubmit}
                open={open}
                width={1220}
                onCancel={(e) => {
                    if (createEditEventLoading) return;
                    onCancel(e);
                }}
                title={modalTitle}
            >
                {eventId ? (
                    eventDataLoading ? (
                        <div className="h-[620px]">
                            <Loader />
                        </div>
                    ) : eventDataError ? (
                        <div className="flex justify-center items-center h-[620px]">
                            <ErrorBox msg={String(eventDataError)} />
                        </div>
                    ) : (
                        <EventDetails
                            nestedAttributePromoMapping={nestedAttributePromoMapping}
                            disabledEvent={disabledEvent}
                            eventId={eventId}
                            open={open}
                            form={form}
                            calenderDataCount={calenderDataCount}
                        />
                    )
                ) : (
                    <EventDetails
                        nestedAttributePromoMapping={nestedAttributePromoMapping}
                        disabledEvent={disabledEvent}
                        eventId={eventId}
                        open={open}
                        form={form}
                        calenderDataCount={calenderDataCount}
                    />
                )}
            </CustomModal>
        </div>
    );
};

export default AddOrEditEventModal;
